import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Globe, Server, Shield, FileCheck, ClipboardCheck, Settings } from 'lucide-react';
import { apiRequest } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { TermsModal } from '../components/TermsModal';

// -------------------- Styled Components --------------------

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #F9FAFB;
  padding: 0;
`;

const ContentContainer = styled.div`
  width: 100%;
  margin: 0;
  padding: 1rem;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ themeMode }) => (themeMode === 'dark' ? '#F9FAFB' : '#374151')};
  margin-bottom: 0.375rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.625rem;
  border: 1px solid ${({ themeMode }) => (themeMode === 'dark' ? '#4B5563' : '#D1D5DB')};
  border-radius: 0.375rem;
  background-color: ${({ themeMode }) => (themeMode === 'dark' ? '#374151' : '#FFFFFF')};
  color: ${({ themeMode }) => (themeMode === 'dark' ? '#F9FAFB' : 'black')};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(58, 79, 237, 0.5);
  }
`;

const ModuleCard = styled.div`
  background-color: ${({ themeMode }) => (themeMode === 'dark' ? '#374151' : '#FFFFFF')};
  border-radius: 0.375rem;
  box-shadow: 0 1px 3px ${({ themeMode }) => (themeMode === 'dark' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.1)')};
  padding: 1rem;
  margin-bottom: 1rem;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 1.25rem;
    margin-bottom: 1.5rem;
  }
`;

const ModuleHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  width: 100%;

  @media (min-width: 768px) {
    margin-bottom: 0.75rem;
  }
`;

const ModuleTitle = styled.h2`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ themeMode }) => (themeMode === 'dark' ? '#F9FAFB' : '#1F2937')};
  margin: 0;
  flex-grow: 1;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const ModuleDescription = styled.p`
  color: ${({ themeMode }) => (themeMode === 'dark' ? '#D1D5DB' : '#4B5563')};
  margin-bottom: 0.75rem;
  font-size: 0.7rem;
  width: 100%;

  @media (min-width: 768px) {
    font-size: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const TestsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;

  @media (min-width: 768px) {
    gap: 0.75rem;
  }
`;

const TestRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  border: 1px solid ${({ severity, themeMode }) =>
    themeMode === 'dark'
      ? getSeverityBorderDark(severity)
      : getSeverityBorder(severity)};
  border-left: 3px solid ${({ severity }) => getSeverityColor(severity)};
  border-radius: 0.375rem;
  transition: background-color 0.2s, transform 0.2s;
  width: 100%;
  box-sizing: border-box;
  background-color: ${({ severity, themeMode }) =>
    themeMode === 'dark'
      ? getSeverityBackgroundDark(severity)
      : getSeverityBackground(severity)};

  &:hover {
    background-color: ${({ themeMode }) =>
    themeMode === 'dark'
      ? 'rgba(30, 144, 255, 0.15)'
      : 'rgba(30, 144, 255, 0.1)'};
    transform: translateY(-1px);
  }

  @media (min-width: 768px) {
    padding: 0.75rem;
  }
`;

const TestInfo = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const TestTitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
`;

const TestTitle = styled.h3`
  font-weight: 500;
  font-size: 0.75rem;
  color: ${({ themeMode }) => (themeMode === 'dark' ? '#F9FAFB' : '#1F2937')};
  margin-right: 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 768px) {
    font-size: 0.875rem;
  }
`;

const TestDescription = styled.p`
  font-size: 0.65rem;
  color: ${({ themeMode }) => (themeMode === 'dark' ? '#9CA3AF' : '#6B7280')};
  margin-top: 0.25rem;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media (min-width: 768px) {
    font-size: 0.75rem;
  }
`;

const ToggleWrapper = styled.div`
  margin-left: 0.5rem;
  flex-shrink: 0;

  @media (min-width: 768px) {
    margin-left: 1rem;
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  min-width: 2rem;

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    display: block;
    width: 2rem;
    height: 1.125rem;
    background-color: #E5E7EB;
    border-radius: 9999px;
    position: relative;
    transition: background-color 0.2s;

    @media (min-width: 768px) {
      width: 2.25rem;
      height: 1.25rem;
    }
  }

  span::after {
    content: "";
    position: absolute;
    top: 2px;
    left: 2px;
    width: 0.875rem;
    height: 0.875rem;
    background-color: #FFFFFF;
    border: 1px solid #D1D5DB;
    border-radius: 50%;
    transition: transform 0.2s;

    @media (min-width: 768px) {
      width: 1rem;
      height: 1rem;
    }
  }

  input:checked + span {
    background: linear-gradient(90deg, #00419F 0%, #1562B7 50%, #1E90FF 100%);
  }

  input:checked + span::after {
    transform: translateX(0.875rem);

    @media (min-width: 768px) {
      transform: translateX(1rem);
    }
  }

  input:focus + span {
    box-shadow: 0 0 0 3px rgba(56, 139, 240, 0.5);
  }
`;

const BottomContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
  width: 100%;

  @media (min-width: 768px) {
    margin-top: 1.5rem;
  }
`;

const StartButton = styled.button`
  padding: 0.5rem 1rem;
  background: #1562B7;
  color: #FFFFFF;
  font-weight: 500;
  font-size: 0.75rem;
  border: none;
  border-radius: 0.375rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: box-shadow 0.2s;
  width: 100%;
  max-width: 200px;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(56, 139, 240, 0.5);
  }

  @media (min-width: 768px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.875rem;
  }
`;

const ProfileTabs = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ themeMode }) => (themeMode === 'dark' ? '#4B5563' : '#E5E7EB')};
  margin-bottom: 1rem;
  overflow-x: auto;
  scrollbar-width: none;
  width: 100%;
  
  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const ProfileTab = styled.button`
  padding: 0.5rem 0.75rem;
  font-size: 0.7rem;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  color: ${({ active }) => (active ? '#00419F' : '#6B7280')};
  background-color: transparent;
  border: none;
  border-bottom: 2px solid ${({ active }) => (active ? '#1562B7' : 'transparent')};
  cursor: pointer;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    color: #1E90FF;
  }

  &:focus {
    outline: none;
  }

  @media (min-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    gap: 0.5rem;
  }

  svg {
    width: 14px;
    height: 14px;

    @media (min-width: 768px) {
      width: 16px;
      height: 16px;
    }
  }
`;

const ComplianceDescription = styled.div`
  padding: 0.5rem;
  background-color: ${({ themeMode }) => (themeMode === 'dark' ? '#2D3748' : '#F3F4F6')};
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  font-size: 0.65rem;
  color: ${({ themeMode }) => (themeMode === 'dark' ? '#D1D5DB' : '#4B5563')};
  width: 100%;

  @media (min-width: 768px) {
    padding: 0.75rem;
    margin-bottom: 1.5rem;
    font-size: 0.75rem;
  }
`;

const ComplianceTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ themeMode }) => (themeMode === 'dark' ? '#F9FAFB' : '#1F2937')};
  margin-bottom: 0.25rem;

  @media (min-width: 768px) {
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  width: 100%;

  @media (min-width: 768px) {
    gap: 1.5rem;
  }
`;

// Severity color functions
function getSeverityColor(severity) {
  switch (severity) {
    case 'Critical':
      return '#EF4444';
    case 'High':
      return '#F97316';
    case 'Medium':
      return '#F59E0B';
    case 'Low':
      return '#3B82F6';
    default:
      return '#6B7280';
  }
}

function getSeverityBorder(severity) {
  switch (severity) {
    case 'Critical':
      return '#FCA5A5';
    case 'High':
      return '#FDBA74';
    case 'Medium':
      return '#FCD34D';
    case 'Low':
      return '#93C5FD';
    default:
      return '#E5E7EB';
  }
}

function getSeverityBorderDark(severity) {
  switch (severity) {
    case 'Critical':
      return '#7F1D1D';
    case 'High':
      return '#7C2D12';
    case 'Medium':
      return '#78350F';
    case 'Low':
      return '#1E3A8A';
    default:
      return '#4B5563';
  }
}

function getSeverityBackground(severity) {
  switch (severity) {
    case 'Critical':
      return '#FEF2F2';
    case 'High':
      return '#FFF7ED';
    case 'Medium':
      return '#FFFBEB';
    case 'Low':
      return '#EFF6FF';
    default:
      return '#FFFFFF';
  }
}

function getSeverityBackgroundDark(severity) {
  switch (severity) {
    case 'Critical':
      return '#450A0A';
    case 'High':
      return '#431407';
    case 'Medium':
      return '#451A03';
    case 'Low':
      return '#172554';
    default:
      return '#374151';
  }
}

// -------------------- Main Component --------------------

export const ScanConfiguration = () => {
  const theme = document.body.getAttribute('data-theme');
  const navigate = useNavigate();

  // State for assets from server
  const [assets, setAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState('');
  const [activeProfile, setActiveProfile] = useState('custom');
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Custom profile modules
  const [scanModules, setScanModules] = useState([
    {
      id: 'website',
      name: 'Web Application Security',
      description: 'OWASP Top 10 vulnerabilities and common web attacks',
      tests: [
        { id: 'sqli', name: 'SQL Injection', description: 'Checks for malicious DB injection via user input.', enabled: true, severity: 'Critical' },
        { id: 'rce', name: 'Remote Code Execution', description: 'Tests if attackers can run commands on the server.', enabled: true, severity: 'Critical' },
        { id: 'xss', name: 'Cross-Site Scripting', description: 'Identifies untrusted script injection in web pages.', enabled: true, severity: 'High' },
        { id: 'ssti', name: 'Server-Side Template Injection', description: 'Checks for template injection flaws in rendering engines.', enabled: true, severity: 'Critical' },
        { id: 'csrf', name: 'Cross-Site Request Forgery', description: 'Detects forced actions through manipulated requests.', enabled: true, severity: 'High' },
        { id: 'dir-traversal', name: 'Directory Traversal', description: 'Attempts to access restricted files via path manipulation.', enabled: true, severity: 'High' },
        { id: 'file-upload', name: 'Arbitrary File Upload', description: 'Checks if malicious files can be uploaded to the server.', enabled: true, severity: 'Critical' },
        { id: 'insecure-deserialization', name: 'Insecure Deserialization', description: 'Detects unsafe object deserialization leading to RCE.', enabled: true, severity: 'Critical' },
        { id: 'clickjacking', name: 'Clickjacking', description: 'Tests for iframes or overlays used to trick user clicks.', enabled: true, severity: 'Medium' },
      ]
    },
    {
      id: 'network',
      name: 'Network Security',
      description: 'Network infrastructure and critical service vulnerabilities',
      tests: [
        { id: 'log4j', name: 'Log4j Vulnerability', description: 'Scans for Log4Shell exploit paths and payloads.', enabled: true, severity: 'Critical' },
        { id: 'smb', name: 'SMB Exploitation', description: 'Detects known SMB flaws like EternalBlue.', enabled: true, severity: 'High' },
        { id: 'ssh', name: 'SSH Security', description: 'Checks SSH configuration and weak authentication.', enabled: true, severity: 'Medium' },
        { id: 'ssl', name: 'SSL/TLS Analysis', description: 'Analyzes certificates, ciphers, and encryption weaknesses.', enabled: true, severity: 'High' },
        { id: 'rdp', name: 'RDP Exploitation', description: 'Scans for known Remote Desktop vulnerabilities and misconfigs.', enabled: true, severity: 'High' },
        { id: 'ftp', name: 'FTP Misconfiguration', description: 'Detects anonymous logins or weak FTP credentials.', enabled: true, severity: 'Medium' },
        { id: 'dns', name: 'DNS Misconfiguration', description: 'Checks for open resolvers or risky DNS zone transfers.', enabled: true, severity: 'Medium' },
        { id: 'snmp', name: 'SNMP Enumeration', description: 'Examines SNMP for publicly readable sensitive info.', enabled: true, severity: 'Low' },
      ]
    }
  ]);

  // Compliance profiles with state management
  const [complianceProfilesData, setComplianceProfilesData] = useState({
    pci: {
      title: 'PCI DSS Compliance',
      description: 'Payment Card Industry Data Security Standard (PCI DSS) is a set of security standards designed to ensure that all companies that accept, process, store or transmit credit card information maintain a secure environment.',
      modules: [
        {
          id: 'pci-network',
          name: 'Network Security',
          description: 'Tests for secure network configuration and protection mechanisms',
          tests: [
            { id: 'firewall', name: 'Firewall Configuration', description: 'Verifies proper firewall implementation and rule sets.', enabled: true, severity: 'Critical' },
            { id: 'ssl-tls', name: 'SSL/TLS Implementation', description: 'Checks for secure TLS versions and cipher suites.', enabled: true, severity: 'High' },
            { id: 'network-segmentation', name: 'Network Segmentation', description: 'Validates proper segmentation of cardholder data environment.', enabled: true, severity: 'Critical' },
          ]
        },
        {
          id: 'pci-data',
          name: 'Data Protection',
          description: 'Validates protection of cardholder data',
          tests: [
            { id: 'encryption', name: 'Data Encryption', description: 'Verifies encryption of sensitive data at rest and in transit.', enabled: true, severity: 'Critical' },
            { id: 'data-retention', name: 'Data Retention', description: 'Checks for proper data retention policies and implementation.', enabled: true, severity: 'High' },
            { id: 'pci-scope', name: 'PCI Scope', description: 'Identifies systems within PCI DSS scope.', enabled: true, severity: 'Medium' },
          ]
        }
      ]
    },
    iso: {
      title: 'ISO 27001 Compliance',
      description: 'ISO 27001 is an international standard for information security management. It provides a framework for organizations to protect their information through the adoption of an Information Security Management System.',
      modules: [
        {
          id: 'iso-access',
          name: 'Access Control',
          description: 'Validates access control mechanisms and policies',
          tests: [
            { id: 'access-policy', name: 'Access Control Policy', description: 'Verifies implementation of access control policies.', enabled: true, severity: 'High' },
            { id: 'privilege-management', name: 'Privilege Management', description: 'Checks for proper privilege allocation and management.', enabled: true, severity: 'High' },
            { id: 'authentication', name: 'Authentication Controls', description: 'Tests strength of authentication mechanisms.', enabled: true, severity: 'Critical' },
          ]
        },
        {
          id: 'iso-cryptography',
          name: 'Cryptography',
          description: 'Evaluates cryptographic controls and implementations',
          tests: [
            { id: 'crypto-policy', name: 'Cryptographic Policy', description: 'Verifies proper cryptographic policy implementation.', enabled: true, severity: 'High' },
            { id: 'key-management', name: 'Key Management', description: 'Checks for secure key management practices.', enabled: true, severity: 'Critical' },
            { id: 'crypto-implementation', name: 'Cryptographic Implementation', description: 'Tests for secure implementation of cryptographic controls.', enabled: true, severity: 'High' },
          ]
        }
      ]
    },
    soc2: {
      title: 'SOC 2 Compliance',
      description: 'SOC 2 is a voluntary compliance standard for service organizations that specifies how organizations should manage customer data based on five "trust service criteria" — security, availability, processing integrity, confidentiality and privacy.',
      modules: [
        {
          id: 'soc2-security',
          name: 'Security Controls',
          description: 'Tests for protection against unauthorized access',
          tests: [
            { id: 'access-controls', name: 'Access Controls', description: 'Verifies proper implementation of access controls.', enabled: true, severity: 'High' },
            { id: 'vulnerability-management', name: 'Vulnerability Management', description: 'Checks for vulnerability management processes.', enabled: true, severity: 'High' },
            { id: 'incident-response', name: 'Incident Response', description: 'Tests incident response capabilities and procedures.', enabled: true, severity: 'Medium' },
          ]
        },
        {
          id: 'soc2-availability',
          name: 'Availability',
          description: 'Validates system availability for operation and use',
          tests: [
            { id: 'performance-monitoring', name: 'Performance Monitoring', description: 'Checks for monitoring of system performance.', enabled: true, severity: 'Medium' },
            { id: 'disaster-recovery', name: 'Disaster Recovery', description: 'Tests disaster recovery procedures and capabilities.', enabled: true, severity: 'High' },
            { id: 'backup-systems', name: 'Backup Systems', description: 'Verifies backup systems and procedures.', enabled: true, severity: 'High' },
          ]
        }
      ]
    }
  });

  // Fetch assets on component mount
  useEffect(() => {
    apiRequest('/api/assets', { method: 'GET' })
      .then(async (res) => {
        if (!res.ok) {
          console.error('Failed to fetch assets:', res.status);
          return;
        }
        const data = await res.json();
        if (data.assets) {
          setAssets(data.assets);
        }
      })
      .catch((err) => console.error('Error fetching assets:', err));
  }, []);

  // Toggle test function for both custom and compliance profiles
  const toggleTest = (moduleId, testId) => {
    if (activeProfile === 'custom') {
      setScanModules(prev =>
        prev.map(m =>
          m.id === moduleId
            ? {
              ...m,
              tests: m.tests.map(t =>
                t.id === testId ? { ...t, enabled: !t.enabled } : t
              )
            }
            : m
        )
      );
    } else {
      // Handle compliance profile toggles
      setComplianceProfilesData(prev => ({
        ...prev,
        [activeProfile]: {
          ...prev[activeProfile],
          modules: prev[activeProfile].modules.map(m =>
            m.id === moduleId
              ? {
                ...m,
                tests: m.tests.map(t =>
                  t.id === testId ? { ...t, enabled: !t.enabled } : t
                )
              }
              : m
          )
        }
      }));
    }
  };

  // Get current profile modules based on selected asset type
  const getCurrentProfileModules = () => {
    if (activeProfile === 'custom') {
      const selectedObj = assets.find((a) => a.name === selectedAsset);
      const selectedType = selectedObj?.assetType;

      if (selectedType === 'website' || selectedType === 'api') {
        return scanModules.filter((m) => m.id === 'website');
      } else if (selectedType === 'network') {
        return scanModules.filter((m) => m.id === 'network');
      }
      return [];
    } else {
      return complianceProfilesData[activeProfile]?.modules || [];
    }
  };

  // Get current profile description
  const getCurrentProfileDescription = () => {
    if (activeProfile === 'custom') {
      return "Custom scan configuration allows you to select specific security tests based on your requirements.";
    } else {
      return complianceProfilesData[activeProfile]?.description || "";
    }
  };

  // Get current profile title
  const getCurrentProfileTitle = () => {
    if (activeProfile === 'custom') {
      return "Custom Scan Configuration";
    } else {
      return complianceProfilesData[activeProfile]?.title || "";
    }
  };

  // Handle scan initiation
  const handleStartScan = async () => {
    try {
      const selectedObj = assets.find((a) => a.name === selectedAsset);
      if (!selectedObj) {
        alert('Please select a valid asset first');
        return;
      }

      const selectedType = selectedObj.assetType;
      let target = '';

      if (selectedType === 'website') {
        target = selectedObj.url;
      } else if (selectedType === 'firewall' || selectedType === 'network') {
        target = (selectedObj.ips && selectedObj.ips.length > 0) ? selectedObj.ips[0] : '';
      }

      if (!target) {
        alert('No valid URL or IP found for selected asset.');
        return;
      }

      // Gather tests based on the active profile
      let testsToSend = [];
      const profileType = activeProfile;

      if (activeProfile === 'custom') {
        const relevantModules = getCurrentProfileModules();
        testsToSend = relevantModules.flatMap(module =>
          module.tests.map(test => ({
            id: test.id,
            enabled: test.enabled,
            severity: test.severity,
            name: test.name,
            module: module.id
          }))
        );
      } else {
        // For compliance profiles
        const profile = complianceProfilesData[activeProfile];
        if (profile) {
          testsToSend = profile.modules.flatMap(module =>
            module.tests.map(test => ({
              id: test.id,
              enabled: test.enabled,
              severity: test.severity,
              name: test.name,
              module: module.id
            }))
          );
        }
      }

      // Include login sequence if available
      const { loginSequence } = selectedObj;

      // Build request body
      const requestBody = {
        type: selectedType,
        target,
        asset: selectedObj,
        profileType,
        scanProfile: testsToSend,
        ...(loginSequence && { loginSequence })
      };

      // Initiate scan
      const res = await apiRequest('/api/scan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error ${res.status}`);
      }

      const data = await res.json();
      navigate(`/report/${selectedType}/${data.scanId}`);

    } catch (err) {
      console.error('Error initiating scan:', err);
      alert(`External domain scanning requires administrator approval. Please contact support at help@zerodayops.com to add this domain to your scope.`);
    }
  };

  return (
    <PageContainer themeMode={theme}>
      <ContentContainer>
        {/* Asset Selection Dropdown */}
        <div style={{ marginBottom: '1rem', width: '100%' }}>
          <Label htmlFor="asset-select" themeMode={theme}>
            Select Asset to Scan
          </Label>
          <Select
            id="asset-select"
            value={selectedAsset}
            onChange={(e) => setSelectedAsset(e.target.value)}
            themeMode={theme}
          >
            <option value="">Select an asset...</option>
            {assets.map((asset) => (
              <option key={asset.name} value={asset.name}>
                {asset.name}
                {asset.url && ` (${asset.url})`}
              </option>
            ))}
          </Select>
        </div>

        {/* Profile Tabs */}
        <ProfileTabs themeMode={theme}>
          <ProfileTab
            active={activeProfile === 'pci'}
            onClick={() => setActiveProfile('pci')}
            themeMode={theme}
          >
            <Shield size={14} />
            PCI DSS
          </ProfileTab>
          <ProfileTab
            active={activeProfile === 'iso'}
            onClick={() => setActiveProfile('iso')}
            themeMode={theme}
          >
            <FileCheck size={14} />
            ISO 27001
          </ProfileTab>
          <ProfileTab
            active={activeProfile === 'soc2'}
            onClick={() => setActiveProfile('soc2')}
            themeMode={theme}
          >
            <ClipboardCheck size={14} />
            SOC 2
          </ProfileTab>
          <ProfileTab
            active={activeProfile === 'custom'}
            onClick={() => setActiveProfile('custom')}
            themeMode={theme}
          >
            <Settings size={14} />
            Custom
          </ProfileTab>
        </ProfileTabs>

        {/* Profile Description */}
        <ComplianceDescription themeMode={theme}>
          <ComplianceTitle themeMode={theme}>{getCurrentProfileTitle()}</ComplianceTitle>
          <p>{getCurrentProfileDescription()}</p>
        </ComplianceDescription>

        {/* Modules and Tests */}
        <GridContainer>
          {getCurrentProfileModules().map((module) => (
            <ModuleCard key={module.id} themeMode={theme}>
              <ModuleHeader>
                {module.id.includes('website') || module.id === 'website' ? (
                  <Globe
                    style={{
                      height: '1rem',
                      width: '1rem',
                      color: '#1562b7',
                      marginRight: '0.5rem',
                    }}
                  />
                ) : (
                  <Server
                    style={{
                      height: '1rem',
                      width: '1rem',
                      color: '#1562b7',
                      marginRight: '0.5rem',
                    }}
                  />
                )}
                <ModuleTitle themeMode={theme}>{module.name}</ModuleTitle>
              </ModuleHeader>

              <ModuleDescription themeMode={theme}>
                {module.description}
              </ModuleDescription>

              <TestsContainer>
                {module.tests.map((test) => (
                  <TestRow
                    key={test.id}
                    themeMode={theme}
                    severity={test.severity}
                  >
                    <TestInfo>
                      <TestTitleRow>
                        <TestTitle themeMode={theme}>{test.name}</TestTitle>
                      </TestTitleRow>
                      <TestDescription themeMode={theme}>
                        {test.description}
                      </TestDescription>
                    </TestInfo>
                    <ToggleWrapper>
                      <ToggleSwitch themeMode={theme}>
                        <input
                          type="checkbox"
                          checked={test.enabled}
                          onChange={() => toggleTest(module.id, test.id)}
                        />
                        <span />
                      </ToggleSwitch>
                    </ToggleWrapper>
                  </TestRow>
                ))}
              </TestsContainer>
            </ModuleCard>
          ))}
        </GridContainer>

        {/* Start Scan Button */}
        <BottomContainer>
          <StartButton onClick={() => setShowTermsModal(true)}>Start Scan</StartButton>
        </BottomContainer>

        {/* Terms Modal */}
        {showTermsModal && (
          <TermsModal
            isOpen={showTermsModal}
            onClose={() => setShowTermsModal(false)}
            onAccept={() => {
              setShowTermsModal(false);
              handleStartScan();
            }}
          />
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default ScanConfiguration;
